'use client'

import { useEffect, useState } from 'react'

type VisionRadarProps = {
	value?: number
	label?: string
	className?: string
}

export function VisionRadar({ value = 86, label = 'VISIÓN', className = '' }: VisionRadarProps) {
	const [displayValue, setDisplayValue] = useState(0)

	useEffect(() => {
		const start = 0
		const duration = 1200
		const startTime = performance.now()

		const animate = (now: number) => {
			const progress = Math.min((now - startTime) / duration, 1)
			const eased = 1 - Math.pow(1 - progress, 3)
			const current = Math.round(start + (value - start) * eased)
			setDisplayValue(current)

			if (progress < 1) {
				requestAnimationFrame(animate)
			}
		}

		requestAnimationFrame(animate)
	}, [value])

	return (
		<div
			className={`relative mx-auto flex h-[280px] w-[280px] items-center justify-center ${className}`}
		>
			{/* Labels */}
			<span className="pointer-events-none absolute top-[72px] left-[18px] animate-[fadeIn_0.5s_ease-out_0.15s_both] text-[9px] font-semibold tracking-[0.18em] text-[var(--brand-secondary)] uppercase">
				Mercadeo
			</span>

			<span className="pointer-events-none absolute top-[72px] right-[14px] animate-[fadeIn_0.5s_ease-out_0.25s_both] text-[9px] font-semibold tracking-[0.18em] text-[var(--brand-secondary)] uppercase">
				Producción
			</span>

			<span className="pointer-events-none absolute top-[160px] left-[12px] animate-[fadeIn_0.5s_ease-out_0.35s_both] text-[9px] font-semibold tracking-[0.18em] text-[var(--brand-secondary)] uppercase">
				Finanzas
			</span>

			<span className="pointer-events-none absolute top-[160px] right-[24px] animate-[fadeIn_0.5s_ease-out_0.45s_both] text-[9px] font-semibold tracking-[0.18em] text-[var(--brand-secondary)] uppercase">
				Ventas
			</span>

			<span className="pointer-events-none absolute bottom-[8px] left-1/2 -translate-x-1/2 animate-[fadeIn_0.5s_ease-out_0.55s_both] text-[9px] font-semibold tracking-[0.18em] whitespace-nowrap text-[var(--brand-secondary)] uppercase">
				Recursos Humanos
			</span>

			{/* SVG */}
			<svg
				viewBox="0 0 250 250"
				className="h-full w-full overflow-visible"
				aria-label={`${value}% ${label}`}
				role="img"
			>
				{/* Base group */}
				<g transform="translate(125 125)">
					{/* Outer subtle pulse */}
					<circle
						r="56"
						fill="none"
						stroke="var(--brand-primary)"
						strokeWidth="1.5"
						opacity="0.12"
						className="animate-[pulseRing_2.6s_ease-in-out_infinite]"
					/>

					{/* Main circle drawn */}
					<circle
						r="54"
						fill="none"
						stroke="var(--brand-primary)"
						strokeWidth="4"
						strokeLinecap="round"
						pathLength="100"
						strokeDasharray="100"
						strokeDashoffset="100"
						className="animate-[drawRing_1s_ease-out_0.15s_forwards]"
					/>

					{/* Blue segments */}
					<circle
						r="54"
						fill="none"
						stroke="var(--brand-primary)"
						strokeWidth="8"
						strokeLinecap="butt"
						pathLength="100"
						strokeDasharray="18 82"
						strokeDashoffset="70"
						className="animate-[revealArc_0.8s_ease-out_0.4s_forwards]"
						opacity="0"
					/>

					<circle
						r="54"
						fill="none"
						stroke="var(--brand-primary)"
						strokeWidth="8"
						strokeLinecap="butt"
						pathLength="100"
						strokeDasharray="14 86"
						strokeDashoffset="42"
						className="animate-[revealArc_0.8s_ease-out_0.55s_forwards]"
						opacity="0"
					/>

					<circle
						r="54"
						fill="none"
						stroke="var(--brand-primary)"
						strokeWidth="8"
						strokeLinecap="butt"
						pathLength="100"
						strokeDasharray="12 88"
						strokeDashoffset="8"
						className="animate-[revealArc_0.8s_ease-out_0.7s_forwards]"
						opacity="0"
					/>

					{/* Ticks */}
					<line
						x1="0"
						y1="-72"
						x2="0"
						y2="-48"
						stroke="var(--brand-primary)"
						strokeWidth="2.5"
						strokeLinecap="round"
						className="animate-[tickIn_0.35s_ease-out_0.7s_both]"
					/>
					<line
						x1="49"
						y1="0"
						x2="72"
						y2="0"
						stroke="var(--brand-primary)"
						strokeWidth="2.5"
						strokeLinecap="round"
						className="animate-[tickIn_0.35s_ease-out_0.82s_both]"
					/>
					<line
						x1="0"
						y1="48"
						x2="0"
						y2="72"
						stroke="var(--brand-primary)"
						strokeWidth="2.5"
						strokeLinecap="round"
						className="animate-[tickIn_0.35s_ease-out_0.94s_both]"
					/>
					<line
						x1="-72"
						y1="0"
						x2="-49"
						y2="0"
						stroke="var(--brand-primary)"
						strokeWidth="2.5"
						strokeLinecap="round"
						className="animate-[tickIn_0.35s_ease-out_1.06s_both]"
					/>

					{/* Diagonal connector to "Mercadeo" */}
					<line
						x1="-46"
						y1="-20"
						x2="-66"
						y2="-34"
						stroke="var(--brand-primary)"
						strokeWidth="2.5"
						strokeLinecap="round"
						className="animate-[tickIn_0.35s_ease-out_1.12s_both]"
					/>

					{/* Center text */}
					<text
						x="0"
						y="4"
						textAnchor="middle"
						className="animate-[fadeUp_0.55s_ease-out_0.85s_both]"
						style={{
							fill: 'var(--brand-secondary)',
							fontSize: '28px',
							fontWeight: 800,
							letterSpacing: '-0.03em',
						}}
					>
						{displayValue}%
					</text>

					<text
						x="0"
						y="28"
						textAnchor="middle"
						className="animate-[fadeUp_0.55s_ease-out_1s_both]"
						style={{
							fill: 'var(--brand-secondary)',
							fontSize: '12px',
							fontWeight: 700,
							letterSpacing: '0.16em',
						}}
					>
						{label}
					</text>
				</g>
			</svg>
		</div>
	)
}
